import React from 'react';
import { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Row, Col, Label } from 'reactstrap';
import CustomSelect from 'components/CustomSelect';
import { showToast } from 'actions/admin/auth';
import { fetchBatchCoursesPlainApi } from 'actions/admin/courses';
import { fetchClassFeeReportApi } from 'actions/admin/financeReport';
import { formResMessage, formatMoney, getGlobalSchoolUtils, getGlobalCurrentBatchUtils } from 'utils/commonFunc';
import ReportTable from 'views/admin/Transport/reports/ReportTable';

const ALL = '';
const STATUS = { PAID: 'Paid', PARTIAL: 'Part paid', UNPAID: 'Not paid' };
const STATUS_OPTIONS = [
  { value: ALL, label: 'All students' },
  ...Object.entries(STATUS).map(([value, label]) => ({ value, label })),
];
const COLUMNS = [
  { key: 'student', label: 'Student' },
  { key: 'total', label: 'Total fee' },
  { key: 'paid', label: 'Paid' },
  { key: 'refund', label: 'Refund' },
  { key: 'balance', label: 'Balance' },
  { key: 'status', label: 'Status' },
  { key: 'guardian', label: 'Guardian' },
  { key: 'mobile', label: 'Mobile' },
];
const sum = (rows, key) => rows.reduce((acc, row) => acc + Number(row.raw[key] || 0), 0);

// Class-wise fee status (Finance tab): pick a class and see which student has paid and which has not, narrowed to one student or
// to Paid / Part paid / Not paid, with the guardian's number to call. Tiles and table always describe the same filtered rows.
const ClassFeeReport = () => {
  const dispatch = useDispatch();
  const token = useSelector(state => state.auth.loggedUserToken);
  const schoolId = getGlobalSchoolUtils()?.schoolId;
  const { batchId } = getGlobalCurrentBatchUtils();
  const [courses, setCourses] = useState([]);
  const [courseId, setCourseId] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [studentId, setStudentId] = useState(ALL);
  const [status, setStatus] = useState(ALL);

  useEffect(() => {
    if (!batchId) return;
    fetchBatchCoursesPlainApi({ batchId, token })
      .then(res => {
        const list = res.data || [];
        setCourses(list);
        setCourseId(current => current || list[0]?.courseId || null);
      })
      .catch(err => dispatch(showToast(formResMessage(err), 'error')));
  }, [batchId]);

  useEffect(() => {
    if (!courseId || !batchId) return undefined;
    let cancelled = false;
    setLoading(true);
    setStudentId(ALL);
    fetchClassFeeReportApi({ token, courseId, batchId })
      .then(res => !cancelled && setRecords(res.data || []))
      .catch(err => {
        if (cancelled) return;
        setRecords([]);
        dispatch(showToast(formResMessage(err), 'error'));
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [courseId, batchId]);

  const rows = useMemo(
    () =>
      records.map(item => ({
        raw: item,
        student: item.studentName,
        total: formatMoney(item.totalFee),
        paid: formatMoney(item.paidAmount),
        refund: formatMoney(item.refundAmount),
        balance: formatMoney(item.balanceAmount),
        status: STATUS[item.paymentStatus] || item.paymentStatus,
        guardian: item.guardianName || '--',
        mobile: item.guardianMobile || '--',
        tones:
          item.paymentStatus === 'UNPAID'
            ? { status: 'bad', balance: 'bad' }
            : item.paymentStatus === 'PARTIAL'
            ? { status: 'warn' }
            : {},
      })),
    [records]
  );
  const studentOptions = useMemo(
    () => [
      { value: ALL, label: 'All students' },
      ...rows.map(row => ({ value: row.raw.studentId, label: row.student })),
    ],
    [rows]
  );
  const shown = rows.filter(
    row => (studentId === ALL || row.raw.studentId === studentId) && (!status || row.raw.paymentStatus === status)
  );
  const count = key => shown.filter(row => row.raw.paymentStatus === key).length;

  const filters = (
    <Row>
      <Col lg={4} md={4} sm={6} xs={12} className="mb-2">
        <Label className="form-label">Class</Label>
        <CustomSelect
          noLabel
          usePortal
          name="classFeeCourse"
          value={courseId}
          options={courses.map(course => ({ value: course.courseId, label: course.name }))}
          onChange={(n, v) => setCourseId(v)}
        />
      </Col>
      <Col lg={4} md={4} sm={6} xs={12} className="mb-2">
        <Label className="form-label">Student</Label>
        <CustomSelect
          noLabel
          usePortal
          name="classFeeStudent"
          value={studentId}
          options={studentOptions}
          onChange={(n, v) => setStudentId(v)}
        />
      </Col>
      <Col lg={4} md={4} sm={6} xs={12} className="mb-2">
        <Label className="form-label">Status</Label>
        <CustomSelect
          noLabel
          usePortal
          name="classFeeStatus"
          value={status}
          options={STATUS_OPTIONS}
          onChange={(n, v) => setStatus(v)}
        />
      </Col>
    </Row>
  );

  if (!schoolId || !batchId) {
    return null;
  }
  return (
    <div>
      <ReportTable
        title="Class-wise fee status"
        fileName="class_fee_status"
        columns={COLUMNS}
        rows={shown}
        filters={filters}
        summary={[
          { label: 'Students', value: shown.length },
          { label: 'Paid', value: count('PAID'), tone: 'good' },
          { label: 'Part paid', value: count('PARTIAL'), tone: 'warn' },
          { label: 'Not paid', value: count('UNPAID'), tone: 'bad' },
          { label: 'Collected', value: formatMoney(sum(shown, 'paidAmount')), tone: 'good' },
          { label: 'Balance', value: formatMoney(sum(shown, 'balanceAmount')), tone: 'bad' },
        ]}
        emptyText="No students for this class and filter."
        loading={loading}
      />
    </div>
  );
};

export default ClassFeeReport;
